import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { t } from '@aquilixshop/i18n';
import { ALIGN_OPTIONS, marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, str, visibilityCss } from '../_shared/blockRender.js';
import { useProductState } from '../_shared/productContext.js';
/**
 * Shrine `share` (preset "Share", category "Buttons") — config-by-config. A share control sitting in the
 * product information column: a "Share" label/trigger that exposes share targets for the current product.
 *
 * SHRINE INVENTORY (6 settings — EVERY one implemented + wired):
 *  - `share_label` (text, root, default "Share") → our `shareLabel` (the trigger/label text). WIRED:
 *    rendered as the visible label next to / on the share control. Empty → hide the label (icons only).
 *  - `featured_image_info` (paragraph, no id) → a Shopify theme-editor INFO note ("the product's featured
 *    image is used as the share preview image"). Non-input help text. REPRODUCED as the matching `info`
 *    string on the `shareLabel` field (we share the product page; OG image = product featured image).
 *  - `title_info` (paragraph, no id) → a second theme-editor INFO note ("the product title + URL are used
 *    as the share text"). Non-input help text. REPRODUCED as `info` on `shareLabel` too (same surface).
 *  - "Margins" (header, no id) → the Margins GROUP (structural divider, not an authoring control) — emitted
 *    by `marginFields` grouping its two ranges under "Margins".
 *  - `margin_top` (range 0–45 step 3 px, def 15) → marginFields top. WIRED via `marginStyle`.
 *  - `margin_bottom` (range 0–45 step 3 px, def 15) → marginFields bottom. WIRED via `marginStyle`.
 *
 * Shrine's Liquid renders the share UI from the SECTION-level product (product/product_images) — the block
 * schema itself carries NO network/icon/mode settings; the two paragraphs document that the section drives
 * the share text/image. Per the section brief ("which networks, icon style/size, label, native trigger"),
 * we add the authoring controls that brief asks for — they're net-new on OUR block, not Shrine settings we
 * skipped, and each maps to a real rendered behaviour:
 *  - `mode` (icons / native) — inline share-target icons, or the device's native share sheet trigger.
 *  - `networks` (multi-checkbox-style text list) — which targets to show: facebook, twitter, pinterest,
 *    whatsapp, email, copy. Honors "which networks".
 *  - `iconStyle` (outline / solid) + `iconSize` (range px) — honors "icon style/size".
 *  - `alignment` — left/center/right placement of the row (Shrine hard-left in the info column).
 *
 * SSR-safe: the FIRST server render is the no-JS fallback — real `<a href>` share-intent links (open in a
 * new tab, work without JS) + a copy-link `<a>` to the product URL. `useState/useEffect` only UPGRADES the
 * experience on the client: a "copied!" confirmation for copy-link, and (mode=native) swapping the link row
 * for a single native-share button when `navigator.share` exists. Degrades fully without JS.
 *
 * DEFERRED (justified Shopify-platform coupling — render the control, wire the real plumbing elsewhere):
 *  - The native Web Share AJAX/`navigator.share` CALL is a browser API invoked client-side; we render the
 *    trigger + call it when present, but there is no Shopify share-app/Liquid hook to reproduce.
 *  - Shrine's reliance on the SECTION's `product` / `product_images` Liquid objects for the OG preview image
 *    is replaced by OUR shared product state (`useProductState`) + `context.resolveUrl` for the share URL.
 *  - `block.shopify_attributes` (Liquid section-renderer attribute) — not a schema setting; no equivalent.
 * (No animation / Material-Symbols glyph-font / CDN-srcset settings exist on this Shrine block, so there are
 * none of those to defer — icons here are inline SVGs, not a glyph font.)
 */
export const shareButtonBlockSchema = {
    type: 'share-button',
    name: 'Share',
    max: 1,
    fields: [
        visibilityField,
        {
            id: 'shareLabel',
            type: 'text',
            label: 'Share label',
            // The two Shrine `paragraph` info notes (featured_image_info + title_info) are reproduced here as the
            // theme-editor help text on the same surface — no input of their own (paragraphs carry no id/value).
            info: "The product's featured image is used as the share preview image; the product title and URL are used as the share text. Leave empty to show icons only.",
            default: 'Partager',
            maxLength: 40,
        },
        {
            id: 'mode',
            type: 'select',
            label: 'Share style',
            group: 'General',
            info: 'Inline icons for each network, or a single button that opens the device share sheet.',
            default: 'icons',
            options: [
                { value: 'icons', label: 'Inline icons' },
                { value: 'native', label: 'Native share button' },
            ],
        },
        {
            id: 'networks',
            type: 'text',
            label: 'Networks',
            group: 'General',
            info: 'Comma-separated list, in order: facebook, twitter, pinterest, whatsapp, email, copy. Used when style is "Inline icons".',
            default: 'facebook, twitter, pinterest, whatsapp, email, copy',
            maxLength: 160,
        },
        {
            id: 'alignment',
            type: 'select',
            label: 'Alignment',
            group: 'General',
            default: 'left',
            options: ALIGN_OPTIONS,
        },
        // ── Icons ──────────────────────────────────────────────────────────────────
        {
            id: 'iconStyle',
            type: 'select',
            label: 'Icon style',
            group: 'Icons',
            default: 'outline',
            options: [
                { value: 'outline', label: 'Outline' },
                { value: 'solid', label: 'Solid' },
            ],
        },
        {
            id: 'iconSize',
            type: 'range',
            label: 'Icon size',
            group: 'Icons',
            min: 16,
            max: 36,
            step: 2,
            default: 22,
            unit: 'px',
        },
        // ── Margins ────────────────────────────────────────────────────────────────
        ...marginFields(15, 15),
    ],
};
const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };
const KNOWN_NETWORKS = ['facebook', 'twitter', 'pinterest', 'whatsapp', 'email', 'copy'];
const ALIASES = {
    facebook: 'facebook',
    fb: 'facebook',
    twitter: 'twitter',
    x: 'twitter',
    pinterest: 'pinterest',
    pin: 'pinterest',
    whatsapp: 'whatsapp',
    wa: 'whatsapp',
    email: 'email',
    mail: 'email',
    copy: 'copy',
    link: 'copy',
    copy_link: 'copy',
    'copy-link': 'copy',
};
/** Parse the free-text `networks` list into ordered, de-duplicated known network keys. */
function parseNetworks(raw) {
    const seen = new Set();
    const out = [];
    for (const part of raw.split(',')) {
        const key = part
            .trim()
            .toLowerCase()
            .replace(/[\s-]+/g, '_');
        const net = ALIASES[key] ?? ALIASES[key.replace(/_/g, '')];
        if (net && !seen.has(net)) {
            seen.add(net);
            out.push(net);
        }
    }
    return out.length ? out : [...KNOWN_NETWORKS];
}
/** Inline-SVG glyph per network (24×24 path set). `solid` fills the badge; `outline` is line-only. */
function NetworkIcon({ net, solid }) {
    const common = {
        width: '60%',
        height: '60%',
        viewBox: '0 0 24 24',
        fill: solid ? 'currentColor' : 'none',
        stroke: solid ? 'none' : 'currentColor',
        strokeWidth: 1.7,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        'aria-hidden': true,
    };
    switch (net) {
        case 'facebook':
            return solid ? (_jsx("svg", { ...common, children: _jsx("path", { d: "M14 8.5h2V5.7h-2.3C11.5 5.7 10 7 10 9v1.6H8v2.8h2V21h3v-7.6h2.2l.4-2.8H13V9.4c0-.6.3-.9 1-.9z" }) })) : (_jsx("svg", { ...common, children: _jsx("path", { d: "M14 8.5h2V5.7h-2.3C11.5 5.7 10 7 10 9v1.6H8v2.8h2V21h3v-7.6h2.2l.4-2.8H13V9.4c0-.6.3-.9 1-.9z" }) }));
        case 'twitter':
            // X/Twitter mark.
            return (_jsx("svg", { ...common, fill: solid ? 'currentColor' : 'none', children: _jsx("path", { d: "M4 4l6.5 8.7L4.3 20H6l5.3-6.1L16 20h4l-6.8-9.1L19.5 4H18l-5 5.7L8.8 4H4z" }) }));
        case 'pinterest':
            return (_jsx("svg", { ...common, children: _jsx("path", { d: "M12 3a9 9 0 0 0-3.3 17.4c-.1-.8-.2-2 0-2.9l1.2-5s-.3-.6-.3-1.5c0-1.4.8-2.4 1.8-2.4.9 0 1.3.7 1.3 1.5 0 .9-.6 2.2-.9 3.5-.2 1 .5 1.9 1.6 1.9 1.9 0 3.2-2.4 3.2-5.2 0-2.2-1.5-3.8-4.1-3.8a4.7 4.7 0 0 0-4.9 4.7c0 .9.3 1.5.7 2 .2.2.2.3.1.6l-.2.9c-.1.3-.3.4-.6.2-1.2-.5-1.8-1.9-1.8-3.5 0-2.6 2.2-5.7 6.6-5.7 3.5 0 5.9 2.6 5.9 5.3 0 3.6-2 6.3-5 6.3-1 0-1.9-.5-2.2-1.1l-.6 2.4c-.2.8-.7 1.7-1.1 2.3A9 9 0 1 0 12 3z" }) }));
        case 'whatsapp':
            return (_jsxs("svg", { ...common, children: [_jsx("path", { d: "M5 19l1-3.4A7 7 0 1 1 9 18.4L5 19z" }), _jsx("path", { d: "M9.2 9.3c.2-.5.4-.5.6-.5h.5c.2 0 .4 0 .6.5l.6 1.4c.1.2 0 .4-.1.5l-.4.5c-.1.2-.2.3-.1.5.2.5.7 1.2 1.3 1.6.6.4.9.5 1.1.5.1 0 .3 0 .4-.2l.4-.5c.2-.2.3-.2.5-.1l1.3.6c.2.1.3.2.3.4 0 .5-.2 1.2-.6 1.4-.4.3-1.3.6-2 .4-1.3-.3-2.7-1.1-3.8-2.9-.7-1.1-.9-2-1-2.6-.1-.6.2-1.1.4-1.4z" })] }));
        case 'email':
            return (_jsxs("svg", { ...common, children: [_jsx("rect", { x: "3.5", y: "5.5", width: "17", height: "13", rx: "2" }), _jsx("path", { d: "M4 7l8 5.5L20 7" })] }));
        case 'copy':
            return (_jsxs("svg", { ...common, children: [_jsx("path", { d: "M9.5 13.5a3 3 0 0 0 4.5.3l2.5-2.5a3 3 0 1 0-4.2-4.2l-1.1 1" }), _jsx("path", { d: "M14.5 10.5a3 3 0 0 0-4.5-.3L7.5 12.7a3 3 0 1 0 4.2 4.2l1-1" })] }));
        default:
            return null;
    }
}
const LABELS = {
    facebook: 'Facebook',
    twitter: 'X',
    pinterest: 'Pinterest',
    whatsapp: 'WhatsApp',
    email: 'Email',
    copy: 'Copy link',
};
/** Build the outbound share-intent href for a network (no-JS fallback target). */
function shareHref(net, url, title, image) {
    const u = encodeURIComponent(url);
    const t = encodeURIComponent(title);
    switch (net) {
        case 'facebook':
            return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
        case 'twitter':
            return `https://twitter.com/intent/tweet?url=${u}&text=${t}`;
        case 'pinterest':
            return `https://pinterest.com/pin/create/button/?url=${u}&description=${t}${image ? `&media=${encodeURIComponent(image)}` : ''}`;
        case 'whatsapp':
            return `https://api.whatsapp.com/send?text=${t}%20${u}`;
        case 'email':
            return `mailto:?subject=${t}&body=${u}`;
        case 'copy':
            // No-JS fallback: a plain link to the product page; JS upgrades this to clipboard copy.
            return url;
    }
}
/**
 * Share control bound to the current product. SSR-safe first render = no-JS share-intent links + a
 * copy-link anchor; the client upgrades copy to clipboard ("Copied!") and, in `native` mode, swaps to a
 * single `navigator.share` button when available.
 */
const ShareButtonBlock = ({ block, context }) => {
    const ps = useProductState();
    // Client-only flags. Initial (SSR) values keep the no-JS fallback; effects upgrade after mount.
    const [mounted, setMounted] = useState(false);
    const [copied, setCopied] = useState(false);
    const [canNativeShare, setCanNativeShare] = useState(false);
    useEffect(() => {
        setMounted(true);
        if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
            setCanNativeShare(true);
        }
    }, []);
    useEffect(() => {
        if (!copied)
            return;
        const t = setTimeout(() => setCopied(false), 1800);
        return () => clearTimeout(t);
    }, [copied]);
    if (!ps)
        return null;
    const bs = block.settings;
    const cls = `aqx-shr-${block.id}`;
    const label = str(bs.shareLabel, 'Partager');
    const mode = str(bs.mode, 'icons');
    const networks = parseNetworks(str(bs.networks, KNOWN_NETWORKS.join(',')));
    const solid = str(bs.iconStyle, 'outline') === 'solid';
    const iconSize = num(bs.iconSize, 22);
    const justify = JUSTIFY[str(bs.alignment, 'left')] ?? 'flex-start';
    // The share URL/title/image come from the shared product state (replaces Shrine's section product object).
    const product = ps.product;
    const shareUrl = context.resolveUrl({ type: 'product', slug: product.slug });
    const shareTitle = product.name;
    const shareImage = product.images?.[0]?.url ?? '';
    const onCopy = (e) => {
        e.preventDefault();
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(shareUrl).then(() => setCopied(true), () => setCopied(false));
        }
    };
    const onNative = () => {
        if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
            void navigator.share({ title: shareTitle, text: shareTitle, url: shareUrl }).catch(() => { });
        }
    };
    // Each share target = a circular icon button (outline ring or solid fill), at the chosen size.
    const css = `.${cls}{display:flex;align-items:center;gap:var(--aqx-theme-space-3, 12px);` +
        `flex-wrap:wrap;justify-content:${justify}}` +
        `.${cls}__label{font-size:var(--aqx-theme-text-sm, 14px);font-weight:600;color:var(--aqx-scheme-text)}` +
        `.${cls}__row{display:flex;align-items:center;gap:8px;flex-wrap:wrap}` +
        `.${cls}__btn{display:inline-flex;align-items:center;justify-content:center;` +
        `inline-size:${iconSize + 14}px;block-size:${iconSize + 14}px;border-radius:50%;` +
        `cursor:pointer;text-decoration:none;transition:background .15s,color .15s,border-color .15s;` +
        (solid
            ? `background:var(--aqx-scheme-text);color:var(--aqx-scheme-bg);border:1px solid var(--aqx-scheme-text)}`
            : `background:transparent;color:var(--aqx-scheme-text);border:1px solid var(--aqx-scheme-border)}`) +
        `.${cls}__btn svg{width:${iconSize}px;height:${iconSize}px}` +
        `.${cls}__btn:hover{color:var(--aqx-scheme-accent);border-color:var(--aqx-scheme-accent)}` +
        (solid
            ? `.${cls}__btn:hover{background:var(--aqx-scheme-accent);color:var(--aqx-scheme-bg)}`
            : '') +
        `.${cls}__native{display:inline-flex;align-items:center;gap:8px;padding:8px 16px;` +
        `border:1px solid var(--aqx-scheme-border);border-radius:var(--aqx-theme-radius-button, 6px);` +
        `background:transparent;color:var(--aqx-scheme-text);cursor:pointer;font:inherit;font-weight:600}` +
        `.${cls}__copied{font-size:var(--aqx-theme-text-sm, 13px);color:var(--aqx-scheme-accent)}` +
        visibilityCss(cls, bs.visibility);
    const wrap = { fontFamily: 'var(--aqx-theme-font)', ...marginStyle(bs, 15, 15) };
    // ── NATIVE mode: once the client confirms navigator.share, render a single native-share button. ──
    // SSR / no-share-support first render falls back to the inline icon row so the control always works.
    const showNative = mode === 'native' && mounted && canNativeShare;
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsxs("div", { className: cls, style: wrap, children: [label ? _jsx("span", { className: `${cls}__label`, children: label }) : null, showNative ? (_jsxs("button", { type: "button", className: `${cls}__native`, onClick: onNative, children: [_jsx(NetworkIcon, { net: "copy", solid: false }), _jsx("span", { children: label || 'Partager' })] })) : (_jsxs("span", { className: `${cls}__row`, children: [networks.map((net) => {
                                if (net === 'copy') {
                                    // Copy-link: a real anchor to the product URL (no-JS fallback) upgraded to clipboard on click.
                                    return (_jsx("a", { className: `${cls}__btn`, href: shareUrl, onClick: onCopy, "aria-label": copied ? t(context, 'product.link_copied') : LABELS[net], title: copied ? 'Copied!' : LABELS[net], children: _jsx(NetworkIcon, { net: net, solid: solid }) }, net));
                                }
                                return (_jsx("a", { className: `${cls}__btn`, href: shareHref(net, shareUrl, shareTitle, shareImage), target: "_blank", rel: "noopener noreferrer", "aria-label": `Share on ${LABELS[net]}`, title: LABELS[net], children: _jsx(NetworkIcon, { net: net, solid: solid }) }, net));
                            }), copied ? (_jsx("span", { className: `${cls}__copied`, role: "status", children: "Copied!" })) : null] }))] })] }));
};
export const shareButtonBlock = {
    type: 'share-button',
    schema: shareButtonBlockSchema,
    Component: ShareButtonBlock,
    meta: { label: 'Share' },
};
//# sourceMappingURL=index.js.map